import cloneDeep from "lodash/cloneDeep";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { FormGroup } from "react-bootstrap-3";
import { FormattedMessage } from "react-intl";
import { useLocaleProvider } from "../../components/locale-provider";
import { SectionHeading } from "../../components/section-heading";
import { messages } from "./messages";
import {
  ButtonContainer,
  CheckAllControlFilter,
  CollapseBody,
  CollapseHeader,
  CollapseListWrapper,
  MessageContainer,
  OptionsTableContainer,
  Placeholder,
  ResultContainer,
  StyledArrowDown,
  StyledCollapseContainer,
  StyledMultiCheckBoxGroup,
} from "./styles";
import { FullWidthLoader } from "../../components/full-width-loader";
import InventoryResultRenderer from "../inventory/multiUpdate/inventoryResultRenderer";
import { BatchInventoryEntity } from "./domain/BatchInventory.entity";
import {
  BatchInventoryResultEntity,
  BatchInventoryResultError,
} from "./domain/BatchInventoryResult.entity";
import { MealPlanObject, useMealPlanApi } from "./infrastructure/api/mealplans.api";
import { useInventoryApi } from "./infrastructure/api/inventory.api";
import { useConditionsApi } from "./infrastructure/api/conditions.api";
import {
  Options,
  usePopulateMultiCheckboxList,
} from "./infrastructure/hooks/populateMultiCheckboxList.hook";
import {
  SmartFilterOptions,
  useFilterOccupationsBySelectedGuest,
} from "./infrastructure/hooks/filterOccupationsBySelectedGuest.hook";
import { AccommodationObject } from "../accommodation/infrastructure/api/accommodation.api";
import { useAccommodationsQuery } from "../accommodation/accommodation.hook";
import { OccupationEntity } from "../occupation/domain/occupation.entity";
import { getAllConfigurations } from "../occupation/infrastructure/occupation.api";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";

export interface BatchInventoryProps {
  propertyTicker: string;
}

interface OperationError {
  message: string;
}

export interface Cancellation {
  propertyTicker: string;
  reservationId: string;
  cancellationResultStatus: string;
}

function BatchInventory({ propertyTicker }: BatchInventoryProps) {
  const propertyConfiguration = usePropertyConfiguration();
  const locale = useLocaleProvider().currentLocale?.substring(0, 2);
  const { data: accommodationsData, isLoading: accommodationsLoading } = useAccommodationsQuery();
  const { getAllMealPlans } = useMealPlanApi();
  const { getConditions } = useConditionsApi();
  const {
    createInventoryLines,
    getInventoryLineValueType,
    getInventoryLineErrorCode,
  } = useInventoryApi();

  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const [result, setResult] = useState<any>(undefined);
  const [originalOptions, setOriginalOptions] = useState<Options>();
  const [options, setOptions] = useState<Options>();
  const [values, setValues] = useState<BatchInventoryEntity>(BatchInventoryEntity.empty());
  const [accommodations, setAccommodations] = useState<AccommodationObject[] | undefined>();
  const [mealPlans, setMealPlans] = useState<MealPlanObject[] | undefined>();
  const [occupations, setOccupations] = useState<OccupationEntity[] | undefined>();
  const [conditions, setConditions] = useState<any[] | undefined>();
  const [smartFilterOpen, setSmartFilterOpen] = useState<boolean>(false);
  const [smartFilterApplied, setSmartFilterApplied] = useState<SmartFilterOptions>({
    adults: false,
    babies: false,
    children: false,
    juniors: false,
    seniors: false,
  });

  const checkboxOptions = usePopulateMultiCheckboxList(
    accommodations,
    mealPlans,
    conditions,
    occupations,
    propertyConfiguration?.occupationType
  );
  const filteredOptions = useFilterOccupationsBySelectedGuest(
    checkboxOptions.list,
    smartFilterApplied,
    occupations
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getAllMealPlans(propertyTicker),
      getConditions(propertyTicker),
      getAllConfigurations(),
    ])
      .then(([mealPlans, conditions, occupations]) => {
        setMealPlans(mealPlans);
        setConditions(conditions);
        setOccupations(occupations);
      })
      .catch(err => setError(err))
      .finally(() => setLoading(false));
  }, [getConditions, getAllMealPlans, propertyTicker]);

  useEffect(() => {
    if (accommodationsData) {
      setAccommodations(accommodationsData);
    }
  }, [accommodationsData]);

  useEffect(() => {
    setOriginalOptions(checkboxOptions.list);
    setOptions(filteredOptions);
    setFetchError(fetchError);
  }, [
    accommodations,
    checkboxOptions,
    conditions,
    fetchError,
    filteredOptions,
    mealPlans,
    occupations,
    options,
    originalOptions,
  ]);

  const onUpdate = useCallback(async () => {
    setLoading(true);
    try {
      const response = await createInventoryLines(values);
      setResult(
        BatchInventoryResultEntity.of({
          createdInventories: response.createdInventories,
          createdVariables: response.createdTickersList,
          errors: response.errorsList.map(error =>
            BatchInventoryResultError.of({
              ticker: error.ticker,
              relatedTickersList: error.relatedTickersList,
              valueType: getInventoryLineValueType(error.valueType),
              errorCode: getInventoryLineErrorCode(error.errorCode),
              errorMessage: error.errorMessage,
            })
          ),
        })
      );
    } catch (err) {
      setError(err as OperationError);
    } finally {
      setLoading(false);
    }
  }, [createInventoryLines, getInventoryLineErrorCode, getInventoryLineValueType, values]);

  const onChange = useCallback(
    val => {
      const vls = cloneDeep(values);
      vls[val.field] = val.value;
      setValues(vls);
    },
    [values]
  );

  const onFiltersChange = useCallback(
    (type: string, key: string, vals: Options) => {
      const vls = cloneDeep(values);
      if (type === "select") {
        vls[key] = Array.isArray(vals) && vals.map(it => it.ticker);
      } else {
        vls[key] = [];
      }
      setValues(vls);
    },
    [values, setValues]
  );

  const handleSmartFilterClick = useCallback(() => {
    setSmartFilterOpen(!smartFilterOpen);
  }, [smartFilterOpen, setSmartFilterOpen]);

  const handleFilterChange = useCallback(
    (event, guestType) => {
      const applied = { ...smartFilterApplied };
      applied[guestType as keyof SmartFilterOptions] = event.target.checked;
      setSmartFilterApplied(applied);
    },
    [smartFilterApplied, setSmartFilterApplied]
  );

  const smartFilter = useMemo(
    () => (
      <StyledCollapseContainer open={smartFilterOpen}>
        <CollapseHeader onClick={handleSmartFilterClick}>
          <FormattedMessage {...messages.guests} /> <StyledArrowDown flip={smartFilterOpen} />{" "}
        </CollapseHeader>
        <CollapseBody open={smartFilterOpen}>
          <CollapseListWrapper>
            {Object.keys(smartFilterApplied).map(guestTypeRaw => {
              const guestType: keyof SmartFilterOptions = guestTypeRaw as keyof SmartFilterOptions;
              return (
                <FormGroup className="form-inline" key={guestTypeRaw}>
                  <div className="checkbox c-checkbox needsclick">
                    <label className="needsclick ">
                      <input
                        type="checkbox"
                        checked={smartFilterApplied[guestType as keyof SmartFilterOptions]}
                        className="needsclick"
                        onChange={event => handleFilterChange(event, guestType)}
                      />
                      <em className="fa fa-check" />
                      <FormattedMessage {...messages[guestType]} />
                    </label>
                  </div>
                </FormGroup>
              );
            })}
          </CollapseListWrapper>
        </CollapseBody>
      </StyledCollapseContainer>
    ),
    [smartFilterOpen, handleSmartFilterClick, smartFilterApplied, handleFilterChange]
  );

  const optionsTable = useMemo(
    () =>
      options &&
      Object.entries(options).map(([keyRaw, vals], idx) => {
        const key: keyof Options = keyRaw as keyof Options;
        return (
          <div>
            <MessageContainer>
              {messages[key] && <FormattedMessage {...messages[key]} />}
            </MessageContainer>
            <CheckAllControlFilter marginBottom={idx === 1}>
              <span onClick={() => onFiltersChange("select", key, vals)}>
                <FormattedMessage {...messages.selectAll} />
              </span>
              <span>{" | "}</span>
              <span onClick={() => onFiltersChange("deselect", key, vals)}>
                {" "}
                <FormattedMessage {...messages.selectNone} />
              </span>
            </CheckAllControlFilter>
            {key === "occupations" && !checkboxOptions.hasAccommodationModel && (
              <>
                <Placeholder />
                {smartFilter}
              </>
            )}
            <StyledMultiCheckBoxGroup
              value={values && values[key]}
              field={{
                fieldName: key,
                options:
                  Array.isArray(vals) &&
                  vals.map(obj => {
                    return {
                      ticker: obj.ticker,
                      name:
                        obj.translations.find(
                          (it: { locale: string; name: string }) => it.locale === locale
                        )?.name || obj.translations[0].name,
                    };
                  }),
              }}
              onChange={onChange}
              restrictedHeight={key === "occupations"}
            />
          </div>
        );
      }),
    [
      options,
      checkboxOptions.hasAccommodationModel,
      smartFilter,
      values,
      onChange,
      onFiltersChange,
      locale,
    ]
  );

  if (isLoading || accommodationsLoading) {
    return <FullWidthLoader />;
  }

  if (fetchError) {
    return <FormattedMessage {...messages.fetchError} />;
  }
  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <OptionsTableContainer className="witadmin-bs3 witadmin-bs3-parent">
          {optionsTable}
        </OptionsTableContainer>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={onUpdate}>
            <FormattedMessage {...messages.create} />
          </Button>
        </ButtonContainer>
        {result && (
          <ResultContainer>
            <Alert variant="success">
              <strong>
                <FormattedMessage {...messages.saveSuccess} />
              </strong>
            </Alert>
            <InventoryResultRenderer inventoryResult={result} />
          </ResultContainer>
        )}
      </Card>
    </>
  );
}

export default BatchInventory;
